import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { useCurrency } from "@/hooks/use-currency";
import { DollarSign, CreditCard, Calendar, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";

interface Payment {
  id: string;
  amount: number;
  currency: string;
  payment_date: string;
  payment_method: string;
  status: string;
  description: string;
}

export const ClientPayments = () => {
  const { formatAmount, convertAmount, currency } = useCurrency();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPaid: 0,
    pendingPayments: 0,
    lastPayment: null as Payment | null,
  });

  useEffect(() => {
    fetchPayments();
  }, [currency]);

  const fetchPayments = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch payments from subscriptions
      const { data: subscriptions } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .eq("payment_status", "completed");

      // Fetch payments from invoices
      const { data: invoices } = await supabase
        .from("invoices")
        .select("*")
        .eq("client_email", user.email)
        .eq("status", "paid");

      // Combine and format payments
      const allPayments: Payment[] = [];

      subscriptions?.forEach((sub) => {
        allPayments.push({
          id: sub.id.toString(),
          amount: sub.price,
          currency: sub.currency || "USD",
          payment_date: sub.created_at,
          payment_method: "Subscription",
          status: "completed",
          description: `${sub.plan_name} - Subscription Payment`,
        });
      });

      invoices?.forEach((inv) => {
        allPayments.push({
          id: inv.id,
          amount: inv.subtotal,
          currency: inv.currency || "USD",
          payment_date: inv.created_at,
          payment_method: "Invoice",
          status: "completed",
          description: `Invoice ${inv.invoice_number}`,
        });
      });

      // Sort by date
      allPayments.sort((a, b) => 
        new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime()
      );

      setPayments(allPayments);

      // Calculate stats
      const totalPaid = allPayments.reduce((sum, payment) => {
        const converted = convertAmount(
          payment.amount,
          payment.currency as any,
          currency
        );
        return sum + converted;
      }, 0);

      setStats({
        totalPaid,
        pendingPayments: 0,
        lastPayment: allPayments[0] || null,
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching payments:", error);
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading payment history...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Payment Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="glass-card p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-green-500/10">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Paid</p>
              <p className="text-2xl font-bold">{formatAmount(stats.totalPaid)}</p>
            </div>
          </div>
        </Card>

        <Card className="glass-card p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-blue-500/10">
              <CreditCard className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Transactions</p>
              <p className="text-2xl font-bold">{payments.length}</p>
            </div>
          </div>
        </Card>

        <Card className="glass-card p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-purple-500/10">
              <Calendar className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Last Payment</p>
              <p className="text-lg font-bold">
                {stats.lastPayment
                  ? format(new Date(stats.lastPayment.payment_date), "MMM dd, yyyy")
                  : "No payments yet"}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Payment History */}
      <Card className="glass-card p-6">
        <h3 className="text-xl font-semibold mb-4">Payment History</h3>
        {payments.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No payment history available yet
          </div>
        ) : (
          <div className="space-y-4">
            {payments.map((payment) => (
              <div
                key={payment.id}
                className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">{payment.description}</p>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(payment.payment_date), "MMM dd, yyyy")} •{" "}
                      {payment.payment_method}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold">
                    {formatAmount(
                      convertAmount(payment.amount, payment.currency as any, currency)
                    )}
                  </p>
                  <p className="text-sm text-green-600">Completed</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
